import React from "react";
import type { BraillerLoanManagementItem } from '../brailler.types';
import {SelectField, ButtonIconEdit, ButtonIconCopyClipboard} from "../../../../Components/Inputs";
import {ButtonIconPackingList} from "../../../../Components/Inputs/Buttons/ButtonIcon/ButtonIcon";
import {getLoanStatusDisplay, getRepairStatusDisplay, isUnloanedRepair} from "./braillerLoanManagementUtility";
import {BRAILLER_LOAN_STATUS_DISPLAY_OPTIONS, BRAILLER_REPAIR_STATUS_DISPLAY_OPTIONS} from "./braillerHistoryConstants";
import {truncateString} from "../../../../Utilities/commonUtilities";

export const BraillerLoanManagementRow = ({
                                              item,
                                              canManageBraillerLoans,
                                              handleOpenEditDialog,
                                              handleCopyToClipboard,
                                              handlePacking,
                                              updateManagementItemInState,
                                          }: {
    item: BraillerLoanManagementItem;
    canManageBraillerLoans: boolean;
    handleOpenEditDialog: (event: React.MouseEvent<HTMLElement>, item: BraillerLoanManagementItem) => void;
    handleCopyToClipboard: () => Promise<void>;
    handlePacking: () => void;
    updateManagementItemInState: (id: string, type: string, status: number | null, notes: string | null, serialNumber?: string | null, issue?: string | null, inventoryNotes?: string | null, resolution?: string | null, repairStatus?: number | null) => void;
}) => {

    const handleStatusChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newStatus = parseInt(event.target.value);
        updateManagementItemInState(item.id, item.type, newStatus, item.notes);
    };

    const renderPatronInfo = () => {
        if (item.student) return item.student.fullName;
        if (isUnloanedRepair(item)) return <em>Unloaned Repair</em>;
        return '-';
    };

    const renderStatus = () => {
        if (!canManageBraillerLoans) {
            return item.type === 'Loan' ? getLoanStatusDisplay(item.loanStatus) : getRepairStatusDisplay(item.repairStatus);
        }

        if (item.type === 'Loan') {
            return (
                <SelectField
                    hideLabel
                    includeDefaultOption={false}
                    label={`Brailler Loan Status for ${item.model || item.catalogTitle}`}
                    name={`brailler-loan-status-${item.id}`}
                    onChange={handleStatusChange}
                    options={BRAILLER_LOAN_STATUS_DISPLAY_OPTIONS}
                    value={item.loanStatus ?? undefined}
                />
            );
        }

        return (
            <SelectField
                hideLabel
                includeDefaultOption={false}
                label={`Brailler Repair Status for ${item.model || item.catalogTitle}`}
                name={`brailler-repair-status-${item.id}`}
                onChange={handleStatusChange}
                options={BRAILLER_REPAIR_STATUS_DISPLAY_OPTIONS}
                    value={item.repairStatus ?? undefined}
            />
        );
    };

    const renderIssueOrNotes = () => {
        if (item.type === 'Repair') {
            return (
                <div>
                    <div><strong>Issue:</strong> {truncateString(item.issue || '') || '-'}</div>
                    {item.resolution && <div><strong>Resolution:</strong> {truncateString(item.resolution)}</div>}
                </div>
            );
        }
        return truncateString(item.notes || '') || '-';
    };

    const isLoanItem = item.type === 'Loan';
    const hasShippingLocation = !!item.shippingLocation || !!item.shippingLocationName;

    // Fixed width for each button slot to maintain alignment
    const buttonSlotStyle = {
        width: '2rem',
        display: 'inline-flex',
        justifyContent: 'center'
    };

    const model = item.model || '-';

    return (
        <tr>
            <td>{item.type}</td>
            <td>{model}</td>
            <td>{item.serialNumber || '-'}</td>
            <td>{item.dateCreatedFormatted}</td>
            <td>{item.dateCheckedOutFormatted}</td>
            <td>{renderPatronInfo()}</td>
            <td>{item.shippingLocationName || '-'}</td>
            <td>{item.patronName}</td>
            <td>{renderStatus()}</td>
            <td>{renderIssueOrNotes()}</td>
            <td className="align-center">
                <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: '2px'}}>
                    <span style={buttonSlotStyle}>
                        {canManageBraillerLoans && (
                            <ButtonIconEdit
                                inputDescription={`for ${model}`}
                                onClick={(event) => event && handleOpenEditDialog(event, item)}
                            />
                        )}
                    </span>
                    <span style={buttonSlotStyle}>
                        {hasShippingLocation && (
                            <ButtonIconCopyClipboard
                                inputDescription={`for ${model}`}
                                onClick={handleCopyToClipboard}
                            />
                        )}
                    </span>
                    <span style={buttonSlotStyle}>
                        {isLoanItem && (
                            <ButtonIconPackingList
                                inputDescription={`for ${model}`}
                                onClick={handlePacking}
                            />
                        )}
                    </span>
                </div>
            </td>
        </tr>
    );
};
